import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import socket from 'shared/socket';
import { etiquetaTotal, escalarEtiquetasExtra } from 'shared/etiquetaUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TERMINAL_STATES = ['entregado', 'rechazado', 'cancelado', 'credito_pagado'];

export function useCart({
  sessionId,
  schemaName,
  sede,
  tasaCambio,
  pedidoInicial,
  pedidosPendientes,
  pedidoActivo,
  pedidoSeleccionadoId,
  onSeleccionarPedido,
  onPedidoCreado,
  onPedidoInicialConsumido,
  mostrarToast,
  ubicacionKey,
  carritoKey,
  pedidoKey,
  puedeModificarEnRevision = false,
  permitirVentaAgotados = false,
  requiereUbicacion = false,
  onRequiereUbicacion,
}) {
  // Clave del carrito aislada POR PEDIDO: cada pedido guarda sus items por separado.
  // Sin pedido seleccionado se usa la clave base (legacy) para el carrito en armado.
  const cartKeyFor = useCallback(
    (pid) => (pid ? `agorax_cart_${sessionId}_${pid}` : carritoKey),
    [sessionId, carritoKey]
  );

  // Hidratar el carrito al montar: si ya hay un pedido seleccionado, recuperar su
  // carrito desde su clave por pedido. Asi, al cambiar de pestana (que desmonta y
  // remonta el catalogo), el pedido NO se reinicia: se restaura desde localStorage.
  const [carrito, setCarrito] = useState(() => {
    const pid = pedidoSeleccionadoId ?? null;
    if (pid && sessionId) {
      try {
        const saved = localStorage.getItem(`agorax_cart_${sessionId}_${pid}`);
        if (saved) return JSON.parse(saved);
      } catch {}
    }
    return [];
  });
  const [pedidoId, setPedidoId] = useState(pedidoSeleccionadoId ?? null);
  const [estadoPedido, setEstadoPedido] = useState(null);
  const [creandoPedido, setCreandoPedido] = useState(false);
  const [confirmarIniciarPedido, setConfirmarIniciarPedido] = useState(null);
  const carritoRef = useRef(carrito);
  carritoRef.current = carrito;
  const pedidoIdRef = useRef(pedidoId);
  pedidoIdRef.current = pedidoId;
  const estadoPedidoRef = useRef(estadoPedido);
  estadoPedidoRef.current = estadoPedido;
  const pedidoInicialConsumido = useRef(false);

  // Sincronizar el pedido seleccionado (fuente de verdad en ClienteHome) hacia el
  // carrito local. Al cambiar de pedido se recarga su carrito desde su clave y, si
  // no hay carrito local, se hidrata desde el servidor.
  useEffect(() => {
    if (pedidoSeleccionadoId === undefined) return;
    const target = pedidoSeleccionadoId ?? null;
    if (parseInt(target || 0, 10) === parseInt(pedidoIdRef.current || 0, 10)) return;
    setPedidoId(target);
    setEstadoPedido(null);
    setCarrito([]);
    carritoRef.current = [];
    if (target && sessionId) {
      const saved = localStorage.getItem(cartKeyFor(target));
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setCarrito(parsed);
          carritoRef.current = parsed;
        } catch {}
        return;
      }
      // Rehidratar desde el servidor (pedido armado en otro dispositivo o tras
      // login, que borra la clave base pero no las claves por pedido).
      (async () => {
        try {
          const res = await axios.get(`${API_URL}/pedidos/mis-pedidos/${schemaName}/${sessionId}`);
          if (res.data.status === 'success') {
            const pedido = (res.data.data || []).find((p) => parseInt(p.id) === parseInt(target, 10));
            if (pedido && pedido.productos) {
              let prods = pedido.productos;
              if (typeof prods === 'string') prods = JSON.parse(prods);
              if (Array.isArray(prods) && prods.length > 0) {
                const cart = prods.map((p) => ({
                  codigo: p.codigo || '',
                  nombre: p.nombre || '',
                  precio: parseFloat(p.precio || 0),
                  precio_bs: 0,
                  cantidad: p.cantidad || 1,
                  stock: 999,
                  pesable: p.pesable || false,
                  imagen_url: p.imagen_url || '',
                  departamento: '',
                  etiquetas: p.etiquetas || [],
                  etiquetas_extra: p.etiquetas_extra || 0,
                  combo_id: p.combo_id || null,
                  componentes: p.componentes || undefined,
                }));
                setCarrito(cart);
                carritoRef.current = cart;
                localStorage.setItem(cartKeyFor(target), JSON.stringify(cart));
              }
            }
          }
        } catch {}
      })();
    }
  }, [pedidoSeleccionadoId, sessionId, cartKeyFor, schemaName]);

  // Persistir el carrito del pedido actual bajo su clave por pedido.
  useEffect(() => {
    if (!sessionId || !pedidoId) return;
    localStorage.setItem(cartKeyFor(pedidoId), JSON.stringify(carrito));
  }, [carrito, sessionId, pedidoId, cartKeyFor]);

  // Pedido inicial (navegar desde Inicio con un pedido concreto): cargar sus items.
  useEffect(() => {
    if (!pedidoInicial || !sessionId) return;
    const productosPedido = pedidoInicial.productos || [];
    const cart = productosPedido.map((p) => ({
      codigo: p.codigo || '',
      nombre: p.nombre || '',
      precio: parseFloat(p.precio || 0),
      precio_bs: 0,
      cantidad: p.cantidad || 1,
      stock: 999,
      pesable: p.pesable || false,
      imagen_url: p.imagen_url || '',
      departamento: '',
      etiquetas: p.etiquetas || [],
      etiquetas_extra: p.etiquetas_extra || 0,
      combo_id: p.combo_id || null,
      componentes: p.componentes || undefined,
    }));
    setCarrito(cart);
    carritoRef.current = cart;
    setPedidoId(pedidoInicial.pedidoId);
    pedidoInicialConsumido.current = true;
    localStorage.setItem(cartKeyFor(pedidoInicial.pedidoId), JSON.stringify(cart));
    if (onPedidoInicialConsumido) onPedidoInicialConsumido();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidoInicial, sessionId, cartKeyFor, onPedidoInicialConsumido]);

  // Sin seleccion explícita: si hay un pedido activo, retomarlo como pedido editable.
  useEffect(() => {
    if (pedidoSeleccionadoId !== undefined) return;
    if (!pedidosPendientes || !sessionId || pedidoInicial) return;
    if (pedidoIdRef.current) return;
    const pedidoActivoCatalogo =
      pedidosPendientes.find((p) => p.session_id === sessionId) || pedidosPendientes[0];
    if (!pedidoActivoCatalogo) return;

    let productos = [];
    try {
      if (typeof pedidoActivoCatalogo.productos === 'string')
        productos = JSON.parse(pedidoActivoCatalogo.productos);
      else if (Array.isArray(pedidoActivoCatalogo.productos))
        productos = pedidoActivoCatalogo.productos;
    } catch {}

    if (productos.length > 0) {
      const cart = productos.map((prod) => ({
        codigo: prod.codigo || '',
        nombre: prod.nombre || '',
        precio: parseFloat(prod.precio || 0),
        precio_bs: 0,
        cantidad: prod.cantidad || 1,
        stock: 999,
        pesable: prod.pesable || false,
        imagen_url: prod.imagen_url || '',
        departamento: '',
        etiquetas: prod.etiquetas || [],
        etiquetas_extra: prod.etiquetas_extra || 0,
        combo_id: prod.combo_id || null,
        componentes: prod.componentes || undefined,
      }));
      setCarrito(cart);
      carritoRef.current = cart;
      localStorage.setItem(cartKeyFor(pedidoActivoCatalogo.id), JSON.stringify(cart));
    }
    setPedidoId(pedidoActivoCatalogo.id);
    if (onSeleccionarPedido) onSeleccionarPedido(pedidoActivoCatalogo.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidosPendientes, pedidoInicial, sessionId, pedidoSeleccionadoId, cartKeyFor]);

  const syncCartToServer = useCallback(async () => {
    const pid = pedidoIdRef.current;
    if (!pid) return false;
    try {
      const cart = carritoRef.current;
      const total = cart.reduce((s, c) => {
        const base = c.pesable ? (c.precio * c.cantidad) / 1000 : c.precio * c.cantidad;
        return s + base + etiquetaTotal(c);
      }, 0);
      const productos = cart.map((c) => ({
        codigo: c.codigo || '',
        nombre: c.nombre || '',
        precio: c.precio || 0,
        cantidad: c.cantidad || 1,
        pesable: c.pesable || false,
        subtotal:
          (c.pesable ? (c.precio * c.cantidad) / 1000 : c.precio * c.cantidad) +
          etiquetaTotal(c),
        imagen_url: c.imagen_url || '',
        etiquetas: (c.etiquetas || []).map((e) => ({
          id: e.id,
          nombre: e.nombre,
          precio: e.precio || 0,
        })),
        etiquetas_extra: c.etiquetas_extra || 0,
        combo_id: c.combo_id || null,
        componentes: c.componentes || null,
      }));
      const res = await axios.put(`${API_URL}/pedidos/${pid}/sync-cart`, {
        schema_name: schemaName,
        productos,
        total,
      });
      localStorage.setItem(cartKeyFor(pid), JSON.stringify(cart));
      if (res.data.status === 'success') return true;
      if (res.data.message) mostrarToast(res.data.message);
      return false;
    } catch {
      return false;
    }
  }, [schemaName, cartKeyFor, mostrarToast]);

  const _insertarEnCarrito = useCallback(async (prod, cant, etiquetasSeleccionadas) => {
    if (!pedidoIdRef.current && requiereUbicacion) {
      const ubicacionGuardada = JSON.parse(localStorage.getItem(ubicacionKey) || '{}');
      if (ubicacionGuardada.lat == null || ubicacionGuardada.lng == null) {
        mostrarToast('Selecciona tu ubicación de entrega para continuar');
        if (onRequiereUbicacion) onRequiereUbicacion();
        return;
      }
    }
    const etiqSel = etiquetasSeleccionadas || [];
    const esCombo = !!prod.combo_id;
    const comboExtraUnit = esCombo ? (prod.combo_extra || 0) : 0;
    const etiqIdsKey = etiqSel.map((e) => e.id).sort().join(',');
    const etiqExtraUnit = etiqSel.reduce((s, e) => s + (e.precio || 0), 0) + comboExtraUnit;
    const prev = carritoRef.current;
    const _firmaCombo = (componentes) =>
      JSON.stringify(
        (componentes || []).map((x) => [
          x.codigo_producto,
          (x.etiquetas || []).map((e) => e.id).sort(),
        ])
      );
    const existe = prev.find((c) => {
      if (c.codigo !== prod.codigo) return false;
      if (esCombo) {
        return _firmaCombo(c.componentes) === _firmaCombo(prod.componentes);
      }
      const cKey = (c.etiquetas || []).map((e) => e.id).sort().join(',');
      return cKey === etiqIdsKey;
    });
    const stockMax = prod.combo_id || permitirVentaAgotados || prod.tipo_producto === 'servicio'
      ? Infinity
      : prod.pesable ? prod.stock * 1000 : prod.stock;
    const nuevaCant = existe
      ? permitirVentaAgotados || prod.tipo_producto === 'servicio'
        ? existe.cantidad + cant
        : Math.min(existe.cantidad + cant, stockMax)
      : permitirVentaAgotados || prod.tipo_producto === 'servicio'
        ? cant
        : Math.min(cant, stockMax);
    const etiqExtraTotal = etiqExtraUnit * nuevaCant;
    const nuevo = existe
      ? prev.map((c) => {
          const coincide = esCombo
            ? _firmaCombo(c.componentes) === _firmaCombo(prod.componentes)
            : (c.etiquetas || []).map((e) => e.id).sort().join(',') === etiqIdsKey;
          if (c.codigo === prod.codigo && coincide)
            return { ...c, cantidad: nuevaCant, etiquetas: etiqSel, etiquetas_extra: etiqExtraUnit * nuevaCant, componentes: prod.componentes };
          return c;
        })
      : [
          ...prev,
          {
            codigo: prod.codigo,
            nombre: prod.descripcion || prod.nombre,
            precio: prod.precio,
            precio_bs: tasaCambio ? prod.precio * tasaCambio : 0,
            cantidad: nuevaCant,
            stock: prod.stock,
            pesable: prod.pesable || false,
            tipo_producto: prod.tipo_producto || 'simple',
            precio_manual: !!prod.precio_manual,
            imagen_url: prod.imagen_url || '',
            departamento: prod.departamento,
            etiquetas: etiqSel,
            etiquetas_extra: etiqExtraTotal,
            combo_id: prod.combo_id || null,
            componentes: esCombo ? prod.componentes : undefined,
          },
        ];
    carritoRef.current = nuevo;
    setCarrito(nuevo);

    socket.emit('cart_update', { schema: schemaName, carrito: nuevo, session_id: sessionId });

    if (!pedidoIdRef.current) {
      setCreandoPedido(true);
      try {
        const item = {
          codigo: prod.codigo,
          nombre: prod.descripcion || prod.nombre,
          cantidad: cant,
          precio: prod.precio,
          pesable: prod.pesable || false,
          imagen_url: prod.imagen_url || '',
          etiquetas: etiqSel.map((e) => ({ id: e.id, nombre: e.nombre, precio: e.precio || 0 })),
          etiquetas_extra: etiqExtraUnit * cant,
          combo_id: prod.combo_id || null,
          componentes: prod.componentes || null,
        };
        const ubicacionData = JSON.parse(localStorage.getItem(ubicacionKey) || '{}');
        const res = await axios.post(`${API_URL}/pedidos/iniciar`, {
          session_id: sessionId,
          schema_name: schemaName,
          lat: ubicacionData.lat,
          lng: ubicacionData.lng,
          direccion_texto: ubicacionData.direccion || 'Ubicacion GPS',
          codigo_sede: sede?.codigo || '0101',
          productos: [item],
        });
        if (res.data.status === 'success') {
          const pid = res.data.data.id;
          setPedidoId(pid);
          if (onSeleccionarPedido) onSeleccionarPedido(pid);
          if (onPedidoCreado) onPedidoCreado();
        } else {
          mostrarToast(res.data.message || 'Error al crear el pedido');
        }
      } catch {
        mostrarToast('Error de conexion al crear el pedido');
      } finally {
        setCreandoPedido(false);
      }
    } else {
      syncCartToServer();
    }

    const cantMostrar = prod.pesable ? (cant / 1000).toFixed(2) + ' kg' : cant + ' und';
    mostrarToast(`Agregaste ${prod.descripcion || prod.nombre} (${cantMostrar})`);
  }, [schemaName, sessionId, sede, tasaCambio, mostrarToast, ubicacionKey, onSeleccionarPedido, onPedidoCreado, syncCartToServer, permitirVentaAgotados, requiereUbicacion, onRequiereUbicacion]);

  const agregarAlCarrito = useCallback(
    async (prod, cant, etiquetasSeleccionadas) => {
      if (!prod.combo_id && prod.tipo_producto !== 'servicio' && !permitirVentaAgotados && (!prod.stock || prod.stock <= 0)) {
        mostrarToast('Este producto esta agotado');
        return;
      }
      if (!pedidoIdRef.current) {
        setConfirmarIniciarPedido({ prod, cant, etiquetas: etiquetasSeleccionadas });
        return;
      }
      // Un pedido que ya paso de revision no es editable: iniciar uno nuevo.
      if (estadoPedidoRef.current && estadoPedidoRef.current !== 'pendiente') {
        const bloqueado = estadoPedidoRef.current !== 'en_revision' ||
          (estadoPedidoRef.current === 'en_revision' && !puedeModificarEnRevision);
        if (bloqueado) {
          if (estadoPedidoRef.current === 'en_revision') {
            mostrarToast('Tu pedido esta en revision y no permite agregar productos. Espera a que el asesor lo apruebe.');
            return;
          }
          setPedidoId(null);
          pedidoIdRef.current = null;
          setEstadoPedido(null);
          estadoPedidoRef.current = null;
          if (onSeleccionarPedido) onSeleccionarPedido(null);
          setCarrito([]);
          carritoRef.current = [];
          setConfirmarIniciarPedido({ prod, cant, etiquetas: etiquetasSeleccionadas });
          return;
        }
      }
      await _insertarEnCarrito(prod, cant, etiquetasSeleccionadas);
    },
    [mostrarToast, _insertarEnCarrito, onSeleccionarPedido, puedeModificarEnRevision, permitirVentaAgotados]
  );

  const eliminarDelCarrito = useCallback(
    (idx) => {
      const nuevo = carritoRef.current.filter((_, i) => i !== idx);
      carritoRef.current = nuevo;
      setCarrito(nuevo);

      socket.emit('cart_update', { schema: schemaName, carrito: nuevo, session_id: sessionId });
      syncCartToServer();
      mostrarToast('Producto eliminado del pedido');
    },
    [syncCartToServer, mostrarToast, schemaName, sessionId]
  );

  const actualizarCantidad = useCallback(
    (idx, delta) => {
      const eliminado = carritoRef.current[idx] && carritoRef.current[idx].cantidad + delta <= 0;
      const nuevo = carritoRef.current
        .map((c, i) => {
          if (i !== idx) return c;
          const stockMax = permitirVentaAgotados || c.combo_id || c.tipo_producto === 'servicio'
            ? Infinity
            : c.pesable ? c.stock * 1000 : c.stock;
          const nueva = Math.min(Math.max(0, c.cantidad + delta), stockMax);
          return { ...c, cantidad: nueva, etiquetas_extra: escalarEtiquetasExtra(c, nueva) };
        })
        .filter((c) => c.cantidad > 0);
      carritoRef.current = nuevo;
      setCarrito(nuevo);

      if (eliminado) {
        mostrarToast('Producto eliminado del pedido');
      }
      socket.emit('cart_update', { schema: schemaName, carrito: nuevo, session_id: sessionId });
      syncCartToServer();
    },
    [syncCartToServer, mostrarToast, schemaName, sessionId, permitirVentaAgotados]
  );

  const irAConfirmarPedido = useCallback(
    async (onConfirmarPedido) => {
      if (carrito.length === 0 || !pedidoIdRef.current) {
        mostrarToast('Agrega productos a tu pedido primero');
        return;
      }
      const ok = await syncCartToServer();
      if (!ok) {
        mostrarToast('Error al sincronizar el pedido. Intenta de nuevo.');
        return;
      }
      if (onConfirmarPedido) onConfirmarPedido();
    },
    [carrito, syncCartToServer, mostrarToast]
  );

  const ejecutarAgregarAlCarrito = useCallback(
    (prod, cant, etiquetasSeleccionadas) => {
      if (estadoPedidoRef.current && estadoPedidoRef.current !== 'pendiente') {
        if (TERMINAL_STATES.includes(estadoPedidoRef.current)) {
          setPedidoId(null); pedidoIdRef.current = null;
          setEstadoPedido(null); estadoPedidoRef.current = null;
          if (onSeleccionarPedido) onSeleccionarPedido(null);
          setCarrito([]); carritoRef.current = [];
          return;
        }
        const bloqueado = estadoPedidoRef.current !== 'en_revision' ||
          (estadoPedidoRef.current === 'en_revision' && !puedeModificarEnRevision);
        if (bloqueado) {
          mostrarToast(`Tu pedido esta en estado "${estadoPedidoRef.current}" y no permite agregar productos.`);
          return;
        }
      }
      _insertarEnCarrito(prod, cant, etiquetasSeleccionadas);
    },
    [mostrarToast, _insertarEnCarrito, onSeleccionarPedido, puedeModificarEnRevision]
  );

  const recargarDesdeServidor = useCallback(async () => {
    const pid = pedidoIdRef.current;
    if (!pid || !sessionId) return;
    try {
      const res = await axios.get(`${API_URL}/pedidos/mis-pedidos/${schemaName}/${sessionId}`);
      if (res.data.status === 'success') {
        const pedidos = res.data.data || [];
        const activo = pedidos.find(p => parseInt(p.id) === parseInt(pid));
        if (activo) {
          if (activo.estado) setEstadoPedido(activo.estado);
          if (activo.productos) {
            let prods = activo.productos;
            if (typeof prods === 'string') prods = JSON.parse(prods);
            if (Array.isArray(prods) && prods.length > 0) {
              const stockExistente = {};
              (carritoRef.current || []).forEach(c => {
                if (c.codigo && c.stock && c.stock !== 99 && c.stock !== 999) {
                  stockExistente[c.codigo] = c.stock;
                }
              });
              const codigos = [...new Set(prods.map(p => p.codigo).filter(Boolean))];
              let stockFresco = {};
              if (codigos.length > 0) {
                try {
                  const sr = await axios.get(`${API_URL}/pedidos/productos/cliente-buscar?q=${encodeURIComponent(codigos.join(' '))}&sede=${sede?.codigo || '0101'}&limite=${codigos.length + 5}&schema_name=${encodeURIComponent(schemaName)}`);
                  if (sr.data.status === 'success') {
                    (sr.data.data || []).forEach(item => { stockFresco[item.codigo] = item.stock ?? 0; });
                  }
                } catch {}
              }
              const cart = prods.map(p => ({
                codigo: p.codigo || '',
                nombre: p.nombre || '',
                precio: parseFloat(p.precio || 0),
                precio_bs: 0,
                cantidad: p.cantidad || 1,
                stock: stockFresco[p.codigo] ?? stockExistente[p.codigo] ?? 99,
                pesable: p.pesable || false,
                imagen_url: p.imagen_url || '',
                departamento: '',
                etiquetas: p.etiquetas || [],
                etiquetas_extra: p.etiquetas_extra || 0,
                combo_id: p.combo_id || null,
                componentes: p.componentes || undefined,
              }));
              setCarrito(cart);
              carritoRef.current = cart;
              localStorage.setItem(cartKeyFor(pid), JSON.stringify(cart));
            }
          }
        }
      }
    } catch {}
  }, [pedidoIdRef, sessionId, schemaName, cartKeyFor, sede?.codigo]);

  return {
    carrito,
    setCarrito,
    pedidoId,
    setPedidoId,
    estadoPedido,
    setEstadoPedido,
    creandoPedido,
    setCreandoPedido,
    confirmarIniciarPedido,
    setConfirmarIniciarPedido,
    carritoRef,
    pedidoIdRef,
    estadoPedidoRef,
    syncCartToServer,
    agregarAlCarrito,
    ejecutarAgregarAlCarrito,
    eliminarDelCarrito,
    actualizarCantidad,
    irAConfirmarPedido,
    recargarDesdeServidor,
    pedidoInicialConsumido,
  };
}
